// LDSupportModalContent.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import PeoplePicker from "components/Common/PeoplePicker/PeoplePicker";
import BackdropComponent from "components/Spinner/Spinner";
import { getPortfolioList } from "services/learnPointUserServices";
import { searchEmployeeProfile } from "services/userServices";

// ─── Styled buttons (unchanged) ────────────────────────────────────────────────
const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": { backgroundColor: "#2878CC" },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  "&:hover": { borderColor: "#3299FF", backgroundColor: "#E0F2FF" },
}));

// ─── Helper: resolve a single OracleId → employee object via the existing API ──
// Returns { OracleId, Name, Email, Display } or null if not found / already null
async function resolveOracleId(oracleId) {
  if (!oracleId) return null;
  try {
    const result = await searchEmployeeProfile(String(oracleId));
    if (
      result?.status &&
      Array.isArray(result.data) &&
      result.data.length > 0
    ) {
      // The proc orders by MatchScore DESC; first result is the best match for an exact OracleId
      const emp = result.data[0];
      return {
        OracleId: emp.OracleId,
        Name: emp.Name,
        Email: emp.Email,
        Display: emp.Display,
      };
    }
  } catch {
    // non-fatal — picker will just render empty
  }
  return null;
}

// ─── Helper: batch-resolve multiple OracleIds with deduplication ───────────────
// Returns a Map<oracleId, employeeObject|null>
async function batchResolveOracleIds(oracleIds) {
  const unique = [...new Set(oracleIds.filter(Boolean))];
  const results = await Promise.allSettled(unique.map(resolveOracleId));
  const map = new Map();
  unique.forEach((id, i) => {
    map.set(id, results[i].status === "fulfilled" ? results[i].value : null);
  });
  return map;
}

// ─── Field config — drives both the form and the picker grid ──────────────────
const LD_FIELDS = [
  {
    key: "ldExecutiveOracleId",
    nameKey: "ldExecutiveName",
    label: "Executive",
  },
  {
    key: "ldLevel1OracleId",
    nameKey: "ldLevel1Name",
    label: "Level 1",
  },
  {
    key: "ldLevel2OracleId",
    nameKey: "ldLevel2Name",
    label: "Level 2",
  },
  { key: "ldLevel3OracleId", nameKey: "ldLevel3Name", label: "Level 3" },
  {
    key: "ldLevel4OracleId",
    nameKey: "ldLevel4Name",
    label: "Level 4",
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
export default function LDSupportModalContent({
  onClose,
  handleSave,
  loading,
  isEdit,
  initialData = {},
}) {
  const [formData, setFormData] = useState({
    ldExecutiveOracleId: null,
    ldLevel1OracleId: null,
    ldLevel2OracleId: null,
    ldLevel3OracleId: null,
    ldLevel4OracleId: null,
    lpPortfolioId: 4,
  });

  // resolvedEmployees: Map<fieldKey, employeeObject|null>
  // Used as the `defaultValue` for each PeoplePicker — object form = zero extra API call
  const [resolvedEmployees, setResolvedEmployees] = useState(new Map());
  const [resolvingEmployees, setResolvingEmployees] = useState(false);

  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const [componentKey, setComponentKey] = useState(0);
  const initialDataLoadedRef = useRef(false);

  // ── 1. Initialise formData once ────────────────────────────────────────────
  useEffect(() => {
    if (initialDataLoadedRef.current) return;
    initialDataLoadedRef.current = true;

    setFormData({
      ldExecutiveOracleId: initialData.ldExecutiveOracleId || null,
      ldLevel1OracleId: initialData.ldLevel1OracleId || null,
      ldLevel2OracleId: initialData.ldLevel2OracleId || null,
      ldLevel3OracleId: initialData.ldLevel3OracleId || null,
      ldLevel4OracleId: initialData.ldLevel4OracleId || null,
      lpPortfolioId: initialData.lpPortfolioId || 4,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 2. Batch-resolve all OracleIds in ONE go when modal opens ──────────────
  //    If the parent already provides Name fields (from Redux setAllValues),
  //    skip the API entirely and build employee objects from those.
  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      const oracleIds = LD_FIELDS.map((f) => initialData[f.key]).filter(
        Boolean,
      );
      if (oracleIds.length === 0) return; // nothing to resolve

      // Fast-path: if the Redux store already shipped the name fields, use them
      const allNamesPresent = LD_FIELDS.every(
        (f) => !initialData[f.key] || initialData[f.nameKey],
      );

      if (allNamesPresent) {
        const map = new Map();
        LD_FIELDS.forEach((f) => {
          if (initialData[f.key]) {
            map.set(f.key, {
              OracleId: initialData[f.key],
              Name: initialData[f.nameKey] || "",
              Email: "",
              Display: initialData[f.nameKey] || "",
            });
          }
        });
        if (!cancelled) setResolvedEmployees(map);
        return;
      }

      // Slow-path: call the API (deduplicated — one call per unique OracleId)
      setResolvingEmployees(true);
      try {
        const idMap = await batchResolveOracleIds(oracleIds);
        if (!cancelled) {
          const map = new Map();
          LD_FIELDS.forEach((f) => {
            if (initialData[f.key]) {
              map.set(f.key, idMap.get(initialData[f.key]) ?? null);
            }
          });
          setResolvedEmployees(map);
        }
      } finally {
        if (!cancelled) setResolvingEmployees(false);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 3. Fetch portfolio list ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fallback = [
      { id: 1, name: "Auto Core" },
      { id: 2, name: "Auto Percepta" },
      { id: 3, name: "BFSI" },
      { id: 4, name: "Healthcare" },
      { id: 5, name: "Public Sector" },
      { id: 6, name: "TMC" },
      { id: 7, name: "Travel & Retail" },
    ];

    const fetch = async () => {
      setLoadingPortfolio(true);
      try {
        const res = await getPortfolioList();
        if (!cancelled) {
          setPortfolioOptions(
            res.status && res.data
              ? res.data.map((p) => ({
                  id: p.lP_PortfolioId || p.LP_PortfolioId,
                  name: p.portfolioName || p.PortfolioName,
                }))
              : fallback,
          );
        }
      } catch {
        if (!cancelled) setPortfolioOptions(fallback);
      } finally {
        if (!cancelled) setLoadingPortfolio(false);
      }
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Cleanup ref on unmount ─────────────────────────────────────────────────
  useEffect(
    () => () => {
      initialDataLoadedRef.current = false;
    },
    [],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleEmployeeChange = useCallback((fieldName, employee) => {
    if (!employee) return;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: employee?.OracleId ?? null,
    }));
  }, []);

  const handleEmployeeReset = useCallback((fieldName) => {
    setFormData((prev) => ({ ...prev, [fieldName]: null }));
  }, []);

  const handlePortfolioChange = (e) => {
    setFormData((prev) => ({ ...prev, lpPortfolioId: e.target.value }));
  };

  const handleSubmit = () => {
    handleSave({
      ldExecutiveOracleId: formData.ldExecutiveOracleId,
      ldLevel1OracleId: formData.ldLevel1OracleId,
      ldLevel2OracleId: formData.ldLevel2OracleId,
      ldLevel3OracleId: formData.ldLevel3OracleId,
      ldLevel4OracleId: formData.ldLevel4OracleId,
      lpPortfolioId: formData.lpPortfolioId,
    });
  };

  const handleReset = () => {
    setFormData({
      ldExecutiveOracleId: initialData.ldExecutiveOracleId || null,
      ldLevel1OracleId: initialData.ldLevel1OracleId || null,
      ldLevel2OracleId: initialData.ldLevel2OracleId || null,
      ldLevel3OracleId: initialData.ldLevel3OracleId || null,
      ldLevel4OracleId: initialData.ldLevel4OracleId || null,
      lpPortfolioId: initialData.lpPortfolioId || 4,
    });
    setComponentKey((prev) => prev + 1);
  };

  const isLoading = loading || resolvingEmployees;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%", maxWidth: 650 }}>
      {isLoading && <BackdropComponent />}

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Urbanist, Verdana, Arial, sans-serif",
            fontSize: 18,
            fontWeight: "bold",
            color: "#3F5765",
            letterSpacing: "0.10em",
          }}
        >
          L&amp;D Support
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        {/* People pickers — driven by LD_FIELDS config */}
        {LD_FIELDS.map((f) => (
          <Grid item xs={12} key={f.key}>
            <Typography
              sx={{
                fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                fontWeight: "bold",
                color: "#3F5765",
                letterSpacing: "0.10em",
                fontSize: 12,
                mb: 1,
              }}
            >
              {f.label}
            </Typography>
            <PeoplePicker
              key={`${f.key}-${componentKey}`}
              label="Search Employee"
              placeholder="Search by name, email, or Oracle ID"
              onChange={(emp) => handleEmployeeChange(f.key, emp)}
              onReset={() => handleEmployeeReset(f.key)}
              // Pass resolved object (no API call) or null (empty picker)
              defaultValue={resolvedEmployees.get(f.key) ?? null}
              isError={false}
              disabled={!isEdit || resolvingEmployees}
            />
          </Grid>
        ))}

        {/* Portfolio dropdown */}
        <Grid item xs={12}>
          <Typography
            sx={{
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              fontWeight: "bold",
              color: "#3F5765",
              letterSpacing: "0.10em",
              fontSize: 12,
              mb: 1,
            }}
          >
            Portfolio
          </Typography>
          <FormControl fullWidth>
            <Select
              size="small"
              value={formData.lpPortfolioId}
              onChange={handlePortfolioChange}
              disabled={!isEdit || loadingPortfolio}
            >
              {portfolioOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Action buttons */}
        <Grid item xs={12}>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
          >
            {isEdit ? (
              <>
                <CustomOutlinedButton
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ minWidth: 85 }}
                >
                  Reset
                </CustomOutlinedButton>
                <CustomButton
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{ minWidth: 85 }}
                >
                  Save
                </CustomButton>
              </>
            ) : (
              <CustomOutlinedButton
                variant="outlined"
                onClick={onClose}
                sx={{ minWidth: 85 }}
              >
                Close
              </CustomOutlinedButton>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

LDSupportModalContent.propTypes = {
  onClose: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool.isRequired,
  initialData: PropTypes.object,
};
